import { Controller } from "@hotwired/stimulus";
import { createPopper } from "@popperjs/core";
import { Loader } from "@googlemaps/js-api-loader";

// Connects to data-controller="locality-autocomplete"
export default class extends Controller {
  static values = {
    autocompleteReady: { type: Boolean, default: false },
    eventListeners: { type: Boolean, default: false },
    noResults: { type: Boolean, default: true },
    warningMessage: String,
    noResultsMessage: String,
    maxPostalCodeLength: Number,
  };

  static targets = ["localityInput", "searchResults", "popperWrapper", "inputWrapper", "searchInput"];

  initialize() {
    this.loader = new Loader({
      apiKey: "AIzaSyAti8pIn2LPbr_7uYCf6FJRFQQ6eZ7LQlw",
      version: "weekly",
    });

    this.loader.importLibrary("places").then(() => {
      this.geocoder = new google.maps.Geocoder();
      this.autocompleteReadyValue = true;
      this.results = [];
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
      this.boundCheckBoundsEvent = this.checkBounds.bind(this);
      this.boundHandleKeydownEvent = this.handleKeydown.bind(this);
      this.popperInstance = createPopper(this.popperWrapperTarget, this.searchResultsTarget, {
        placement: "bottom-start",
        modifiers: [
          {
            name: "sameWidth",
            enabled: true,
            fn: (event) => {
              const state = event.state;
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            phase: "beforeWrite",
            requires: ["computeStyles"],
          },
          {
            name: "flip",
            options: {
              padding: {
                top: 5,
              },
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 5],
            },
          },
        ],
      });
    });
  }

  throttledSearch() {
    if (!this.autocompleteReadyValue) return;

    const value = event.currentTarget.value;
    const countryCode = event.params.countryCode;

    clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      if (value.length < 4 || value.length > this.maxPostalCodeLengthValue) {
        this.searchResultsTarget.innerHTML = "";
        this.results = [];
        return;
      }

      this.eventListenersValue = false;

      this.search(value, countryCode);
    }, 300);
  }

  showResults() {
    if (Object.keys(this.results).length === 0) return; // No results are available, no need to show old results
    this.populateResults();
  }

  search(query, countryCode) {
    this.inputWrapperTarget.classList.add("autocomplete--loading");

    const request = {
      region: countryCode,
      language: document.documentElement.lang,
      componentRestrictions: {
        country: countryCode,
        postalCode: query,
      },
    };

    this.geocoder.geocode(request, (results, status) => {
      this.results = [];

      if (status === google.maps.GeocoderStatus.OK) {
        const localities = results
          .filter((result) =>
            result.address_components.find((component) =>
              component.types.some((type) => ["locality", "political", "administrative_area_level_1", "sublocality", "sublocality_level_1"].includes(type))
            )
          )
          .map(
            (result) =>
              result.address_components.find((component) =>
                component.types.some((type) => ["locality", "political", "administrative_area_level_1", "sublocality", "sublocality_level_1"].includes(type))
              )?.long_name
          )
          .filter(Boolean); // Only keep entries that are not undefined or null

        switch (localities.length) {
          case 0:
            window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "info", message: this.noResultsMessageValue } }));
            break;
          case 1:
            this.localityInputTarget.value = localities[0];
            break;
          default:
            this.results = localities;
            this.localityInputTarget.click();
            break;
        }
      } else {
        console.error("Error fetching localities:", status);
        window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "warning", message: this.warningMessageValue } }));
      }

      this.inputWrapperTarget.classList.remove("autocomplete--loading");
    });
  }

  populateResults() {
    this.searchResultsTarget.innerHTML = "";
    this.popperInstance.update();

    if (this.results.length === 0) {
      window.dispatchEvent(new CustomEvent("flash:create", { detail: { type: "info", message: this.noResultsMessageValue } }));
      return;
    }

    this.eventListenersValue = true;

    this.results.forEach((address, index) => {
      const addressOption = document.createElement("div");
      addressOption.innerText = address;

      if (index === 0) {
        addressOption.classList.add("selected");
      }

      addressOption.classList.add("result", "locality");
      addressOption.addEventListener("click", () => {
        this.localityInputTarget.value = address;
        this.searchResultsTarget.innerHTML = "";
      });

      this.searchResultsTarget.appendChild(addressOption);
    });
  }

  eventListenersValueChanged(value, oldValue) {
    if (value === oldValue) return;

    this.arrowKeysIndex = 0;

    if (value) {
      document.addEventListener("keydown", this.boundHandleKeydownEvent);
      document.addEventListener("click", this.boundCheckBoundsEvent);
    } else {
      document.removeEventListener("keydown", this.boundHandleKeydownEvent);
      document.removeEventListener("click", this.boundCheckBoundsEvent);
    }
  }

  checkBounds(event) {
    if (event.target === this.localityInputTarget) return;
    if (!this.searchResultsTarget.contains(event.target)) {
      this.searchResultsTarget.innerHTML = "";
      this.eventListenersValue = false;
    }
  }

  handleKeydown(event) {
    const key = event.key;

    if (!["ArrowDown", "ArrowUp", "Enter"].includes(key)) return;

    event.preventDefault();
    const maxIndex = this.searchResultsTarget.children.length - 1;

    if (key === "ArrowDown") {
      this.arrowKeysIndex = this.arrowKeysIndex === maxIndex ? 0 : this.arrowKeysIndex + 1;
    } else if (key === "ArrowUp") {
      this.arrowKeysIndex = this.arrowKeysIndex === 0 ? maxIndex : this.arrowKeysIndex - 1;
    } else if (key === "Enter") {
      this.searchResultsTarget.children[this.arrowKeysIndex]?.click();
    }

    Array.from(this.searchResultsTarget.children).forEach((child, index) => {
      child.classList.toggle("selected", index === this.arrowKeysIndex);
    });
  }
}
